import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Circle, Truck, PackageCheck, Package, Home, Search, Calendar, ChevronRight } from "lucide-react";

const STEPS = [
  { k: "placed", label: "Placed", icon: Circle },
  { k: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { k: "packed", label: "Packed", icon: Package },
  { k: "shipped", label: "Shipped", icon: PackageCheck },
  { k: "out_for_delivery", label: "Out for delivery", icon: Truck },
  { k: "delivered", label: "Delivered", icon: Home },
];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [num, setNum] = useState(params.get("n") || "");
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [order, setOrder] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOrder = async (n = num) => {
    if (!n) return toast.error("Enter your order number");
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${n}`);
      setOrder(data);
    } catch { toast.error("Order not found"); setOrder(null); }
    setLoading(false);
  };

  // Load logged-in customer's order history for filter search
  useEffect(() => {
    if (user) api.get("/orders").then((r) => setMyOrders(r.data)).catch(() => setMyOrders([]));
  }, [user]);

  useEffect(() => {
    const initial = params.get("n");
    if (initial) fetchOrder(initial);
  }, [params]);

  const filtered = useMemo(() => {
    if (!user) return [];
    const q = query.trim().toLowerCase();
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(to).getTime() + 24 * 3600 * 1000 : null;
    return myOrders.filter((o) => {
      if (fromTs && new Date(o.created_at).getTime() < fromTs) return false;
      if (toTs && new Date(o.created_at).getTime() > toTs) return false;
      if (q) {
        const hit = o.order_number.toLowerCase().includes(q) ||
          (o.items || []).some((it) => (it.name || "").toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
  }, [user, myOrders, query, from, to]);

  const hasFilter = query || from || to;
  const currentIdx = order ? STEPS.findIndex((s) => s.k === order.status) : -1;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="eyebrow">Tracking</div>
      <h1 className="mt-3 font-display text-5xl text-ink-900">Where&apos;s my order?</h1>

      {/* Exact order lookup */}
      <div className="mt-8 flex gap-3">
        <Input placeholder="Order number, e.g. IM260215-2841" value={num} onChange={(e) => setNum(e.target.value.toUpperCase())} className="rounded-none h-12 font-mono tracking-widest" data-testid="track-input" />
        <button onClick={() => fetchOrder()} disabled={loading} className="btn-primary-ink px-6 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="track-btn">
          {loading ? "…" : "Track"}
        </button>
      </div>

      {/* Filter search (signed-in customers) */}
      {user && (
        <div className="mt-8 border hairline p-5 bg-cream-50" data-testid="track-filters">
          <div className="eyebrow flex items-center gap-2"><Search size={12} /> Or search your order history</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Product or order #" className="rounded-none h-11 pl-9" data-testid="track-search" />
            </div>
            <div className="relative">
              <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none h-11 pl-9" data-testid="track-from" />
            </div>
            <div className="relative">
              <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none h-11 pl-9" data-testid="track-to" />
            </div>
          </div>
          {hasFilter && (
            <div className="mt-4 space-y-2">
              {filtered.length === 0 && <div className="text-sm text-ink-500" data-testid="track-empty">No matching orders.</div>}
              {filtered.map((o) => (
                <button
                  key={o.id}
                  onClick={() => { setNum(o.order_number); fetchOrder(o.order_number); }}
                  className="w-full flex items-center justify-between border hairline p-3 bg-white hover:bg-cream-100 text-left"
                  data-testid={`track-hit-${o.order_number}`}
                >
                  <div>
                    <div className="font-mono text-sm">#{o.order_number}</div>
                    <div className="text-[11px] text-ink-500 mt-0.5">
                      {new Date(o.created_at).toLocaleDateString()} · {o.items?.[0]?.name}{o.items?.length > 1 ? ` +${o.items.length - 1}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ink-500">₹{o.total.toFixed(0)}</span>
                    <ChevronRight size={14} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {order && (
        <div className="mt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border hairline p-6 bg-cream-50">
            <div>
              <div className="eyebrow">Order</div>
              <div className="mt-1 font-mono text-sm">#{order.order_number}</div>
            </div>
            <div>
              <div className="eyebrow">ETA</div>
              <div className="mt-1 font-display text-lg">{order.eta || "—"}</div>
            </div>
            <div>
              <div className="eyebrow">Total</div>
              <div className="mt-1 font-display text-lg">₹{order.total.toFixed(0)}</div>
            </div>
          </div>

          {/* Stepper */}
          <div className="mt-10 overflow-x-auto no-scrollbar">
            <div className="flex justify-between items-start min-w-[520px] sm:min-w-0">
              {STEPS.map((s, i) => {
                const active = i <= currentIdx;
                const Icon = s.icon;
                return (
                  <div key={s.k} className="flex-1 text-center relative px-1">
                    {i > 0 && <div className={"absolute top-4 -left-1/2 right-1/2 h-[2px] " + (i <= currentIdx ? "bg-ink-900" : "bg-ink-300")} />}
                    <div className={"relative inline-flex items-center justify-center w-8 sm:w-9 h-8 sm:h-9 rounded-full border-2 " + (active ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-300 border-ink-300")}>
                      <Icon size={14} />
                    </div>
                    <div className={"mt-3 text-[9px] sm:text-[10px] uppercase tracking-widest leading-tight " + (active ? "text-ink-900" : "text-ink-300")}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {order.tracking_notes?.length > 0 && (
            <div className="mt-10 border hairline p-6 bg-cream-50">
              <div className="eyebrow">Activity</div>
              <ul className="mt-4 space-y-3 text-sm">
                {[...order.tracking_notes].reverse().map((n, i) => (
                  <li key={i} className="flex justify-between border-b hairline pb-2">
                    <div>
                      <div className="text-ink-900 capitalize">{n.status.replace(/_/g, " ")}</div>
                      {n.note && <div className="text-ink-500 text-xs">{n.note}</div>}
                    </div>
                    <div className="text-xs text-ink-500">{new Date(n.at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
